import { AHSeparator, useLayoutContext } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useLazyLoadQuery, graphql } from "react-relay";

import { useSizeClass } from "../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import {
  ListSectionHeader,
  ListSectionHeaderDescription,
  ListSectionHeaderTitle,
} from "../../../components/atoms/ListSectionHeader";
import { KPITeamGroup } from "../Edit/KPITeamGroup";
import { TeamGroupItem } from "../components/TeamGroupItem";
import { KPICreateSubSectionButton } from "../components/KPICreateSubSectionButton";
import { KPICreateSectionsQuery } from "./__generated__/KPICreateSectionsQuery.graphql";
import { FormattedMessage } from "react-intl";
import { useNavigation } from "@react-navigation/native";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorParamList,
} from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";

type Props = {
  params: Required<UserLoggedInNavigatorParamList["KPICreate"]>;
  setSelectedTeams: (
    update: KPITeamGroup[] | ((sections: KPITeamGroup[]) => KPITeamGroup[])
  ) => void;
};

const KPI_CREATE_SECTIONS_QUERY = graphql`
  query KPICreateSectionsQuery {
    currentStore @required(action: THROW) {
      publicTeams {
        id
        name
        icon
        color
      }
    }
  }
`;

export function KPICreateSections(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => <InnerKPICreateSections {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerKPICreateSections({
  params,
  setSelectedTeams,
  fetchKey,
}: Props & { fetchKey: string }) {
  const navigation =
    useNavigation<UserLoggedInNavigatorNavigationProp<"KPICreate">>();
  const data = useLazyLoadQuery<KPICreateSectionsQuery>(
    KPI_CREATE_SECTIONS_QUERY,
    {},
    { fetchKey, fetchPolicy: "store-and-network" }
  );

  const sections = useMemo(() => {
    return data.currentStore.publicTeams.map((t) => {
      const teamGroup = params.teamGroups.find(({ teamId }) => teamId === t.id);
      if (teamGroup) {
        return { ...teamGroup, selected: true };
      } else {
        return {
          color: t.color,
          icon: t.icon,
          teamId: t.id,
          name: t.name,
          subTeams: [],
          goalValue: 0,
          selected: false,
        };
      }
    }) as (KPITeamGroup & { selected: boolean })[];
  }, [params.teamGroups, data.currentStore.publicTeams]);

  const insets = useSafeAreaInsets();

  const layout = useLayoutContext();

  const renderItem = useCallback(
    ({
      item,
      index,
    }: {
      item: KPITeamGroup & { selected: boolean };
      index: number;
    }) => (
      <TeamGroupItem
        item={item}
        isFirst={index === 0}
        isLast={index === sections.length - 1}
        selected={item.selected}
        setSelectedTeams={setSelectedTeams}
        openCreateSubSectionBottomSheet={() => {
          navigation.navigateDeprecated("KPICreateSubTeam", {
            screen: "KPICreateSubTeamSetName",
            params: {
              type: "Create",
              ...params,
              teamId: item.teamId,
              name: "",
            },
          });
        }}
      />
    ),
    [navigation, params, sections.length, setSelectedTeams]
  );

  const padding = layout.space.medium;
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const contentContainerStyle = useMemo(
    () => ({
      padding,
      paddingBottom:
        (isCompact ? insets.bottom + padding : padding) + layout.space.large,
    }),
    [insets.bottom, isCompact, layout.space.large, padding]
  );

  const ListFooterComponent = useMemo(
    () => (
      <KPICreateSubSectionButton
        onPress={() => {
          navigation.navigateDeprecated("KPICreateSubTeam", {
            screen: "KPICreateSubTeamSelectTeam",
            params: {
              type: "Create",
              ...params,
            },
          });
        }}
      />
    ),
    [navigation, params]
  );

  return (
    <FlatList
      contentInsetAdjustmentBehavior="always"
      ItemSeparatorComponent={AHSeparator}
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={ListFooterComponent}
      contentContainerStyle={contentContainerStyle}
      data={sections}
      renderItem={renderItem}
    />
  );
}

const ListHeaderComponent = () => {
  return (
    <ListSectionHeader
      style={{ flexDirection: "column", alignItems: "flex-start" }}
    >
      <ListSectionHeaderTitle>
        <FormattedMessage
          defaultMessage="Select Teams"
          description="KPI select teams header title"
        />
      </ListSectionHeaderTitle>
      <ListSectionHeaderDescription>
        <FormattedMessage
          defaultMessage="Selected teams will be requested to track this KPI"
          description="KPI select teams header description"
        />
      </ListSectionHeaderDescription>
    </ListSectionHeader>
  );
};
